<template>
	<AppDialog
		height="200"
		closeTop="-30"
		max-width="520"
		closeRight="-2"
		disable-max-width
		ref="dialog_container"
		:dialogName="'EditApiDocDialog'"
	>
		<Col class="dialog-content-container">
			<div v-if="openedFromConvScreen">
				<h2 class="text-align-center mb-7">API documentation</h2>
				<AddApiDocumentation
					class="add-api-docs"
					:selectedApiDocs.sync="selectedApiDocs"
					:apiDocNameChange.sync="selectedApiDocName"
					@deleteApiDoc="deleteApiDoc"
				/>
			</div>
			<div v-else class="dialog-content">
				<h3 class="flex-centerize mt-5">{{ isEditMode ? 'Edit' : 'Add' }} API documentation</h3>
				<Row class="flex-centerize mt-7">
					<EditApiDocRadioMenu @change-background-mode="(v) => (backgroundMode = v)" :backgroundMode="backgroundMode" :modes="modes" />
				</Row>
				<div class="mt-6 mx-5">
					<p class="mb-1">Name this api</p>
					<AppTextField outlined hide-details v-model="serviceNameField" placeholder="SalesHub" />
					<span v-if="backgroundMode === URL_MODE">
						<p class="mt-4 mb-1">URL</p>
						<AppTextField
							:rules="validationRules.url"
							:hideDetails="false"
							outlined
							hide-details
							v-model="urlField"
							placeholder="https://docs.saleshub.com/api"
						/>
						<!-- In design but not currently used/implemented code-wise -->
						<!-- <a @click="prompt" href="#">[+] Add a separate link for authentication or related info</a> -->
					</span>
					<span v-else-if="backgroundMode === PASTE_DOCS_MODE"
						><p class="mt-4 mb-1">Documentation</p>
						<AppTextArea
							rows="4"
							v-model="documentationField"
							outlined
							placeholder="https://docs.saleshub.com/api"
							:rules="validationRules.documentation"
							no-resize
							dense
							solo
						/>
					</span>
				</div>
			</div>
			<Row class="flex-justify-end mb-5">
				<AppTooltip position="bottom" :text="doneBtnTooltip">
					<PrimaryButton class="primary-button" :disabled="doneDisabled || isLoading" :loading="isLoading" @click="doneClicked"
						>Done</PrimaryButton
					>
				</AppTooltip>
			</Row>
		</Col>
	</AppDialog>
</template>

<script>
import isUrl from 'is-url';

const URL_MODE = 'url_mode';
const PASTE_DOCS_MODE = 'paste_docs_mode';
import AddApiDocumentation from '@/components/CodeBlockAiTabs/ApiDocsComponents/AddApiDocumentation';
import EditApiDocRadioMenu from '@/components/CodeBlockAiTabs/ApiDocsComponents/EditApiDocRadioMenu';
import AppDialog from '@/components/dialogs/AppDialog/AppDialog';
import AppDialogManagerService from '@/components/dialogs/AppDialogManager/app-dialog-manager-service';
import { deleteAiAssistantApiDoc, updateAiAssistantApiDoc } from '@/store/modules/aiAssistantApiDocs/actions';
import { mapActionsShorthand } from '@/store/utils/mappers';

export default {
	name: 'EditApiDocDialog',
	components: { AppDialog, EditApiDocRadioMenu, AddApiDocumentation },
	props: { apiDoc: { type: Object }, dialogPromiseHandlers: { type: Object }, openedFromConvScreen: { type: Boolean, default: false } },
	data() {
		const isEditMode = !!this.apiDoc;
		const backgroundMode = isEditMode ? (this.apiDoc.url ? URL_MODE : PASTE_DOCS_MODE) : URL_MODE; // Defaults first to url_mode
		return {
			isLoading: false,
			selectedApiDocs: null,
			selectedApiDocName: '',
			URL_MODE,
			PASTE_DOCS_MODE,
			backgroundMode,
			isEditMode,
			urlField: isEditMode ? this.apiDoc.url : '',
			serviceNameField: isEditMode ? this.apiDoc.service_name : '',
			documentationField: isEditMode ? this.apiDoc.pasted_documentation : '',
			validationRules: {
				url: [(val) => isUrl(val) || 'Invalid URL, make sure you include (http/https)'],
				documentation: [
					(str) => {
						if (str.length < 32768) {
							return true;
						}
						// toasting this since there seems to be no good combination of AppTextField and AppTextArea that lets you: 1. control height through 'rows' attr 2. Have a validation error MESSAGE, like in AppTextField
						this.$toast.error('Documentation should be less than 32768 characters', { timeout: 5000 });
						return false;
					},
				],
			},
		};
	},
	computed: {
		doneBtnTooltip() {
			if (this.urlField && this.documentationField) {
				return 'Please fill either "URL" or "Documentation", not both.';
			}
			return '';
		},
		doneDisabled() {
			if (this.selectedApiDocName) {
				return false;
			}
			if (this.urlField && this.documentationField) {
				return true;
			}
			if (this.backgroundMode === this.URL_MODE) {
				return !this.urlField || !this.serviceNameField || !isUrl(this.urlField);
			} else if (this.backgroundMode === this.PASTE_DOCS_MODE) {
				return !this.documentationField || !this.serviceNameField;
			} else {
				console.error('unknown mode', this.backgroundMode);
				return true;
			}
		},
		modes() {
			return [
				{ text: 'Link to documentation', value: this.URL_MODE },
				{ text: 'Paste documentation', value: this.PASTE_DOCS_MODE },
			];
		},
	},
	methods: {
		...mapActionsShorthand({ deleteAiAssistantApiDoc, updateAiAssistantApiDoc }),
		async saveApiDoc(apiDoc) {
			try {
				await this.updateAiAssistantApiDoc({ apiDoc });
			} catch (err) {
				console.error(`Error while updating api doc: ${JSON.stringify(err)}`);
				this.$toast.error(`Failed to update api docs name: ${err.response?.data?.error?.msg}`);
			}
		},
		async deleteApiDoc(id) {
			await this.deleteAiAssistantApiDoc({ id });
		},
		async doneClicked() {
			this.isLoading = true;
			const apiDoc = { service_name: this.serviceNameField || this.selectedApiDocName, id: this.selectedApiDocs || this.apiDoc?.id }; // id is only for update, not for create. Should be removed from the payload if not needed
			if (this.openedFromConvScreen) {
				this.dialogPromiseHandlers.resolve(apiDoc);
				AppDialogManagerService.closeCurrentDialog();
				return;
			}
			if (this.backgroundMode === this.URL_MODE) {
				apiDoc.url = this.urlField;
				apiDoc.pasted_documentation = '';
			} else if (this.backgroundMode === this.PASTE_DOCS_MODE) {
				apiDoc.pasted_documentation = this.documentationField;
				apiDoc.url = '';
			} else {
				console.error('unknown mode', this.backgroundMode);
				this.$toast.error('Unknown mode, should be either "Paste documentation" or "Link to documentation"');
			}
			if (this.urlField && this.documentationField) {
				this.$toast.error('Please fill either "URL" or "Documentation", not both.');
				return;
			}
			if (this.isEditMode) {
				await this.saveApiDoc(apiDoc);
			}
			this.dialogPromiseHandlers.resolve(apiDoc);
			AppDialogManagerService.closeCurrentDialog();
		},
	},
};
</script>

<style scoped lang="scss">
.dialog-content-container:has(.add-api-docs) {
	height: 200px;
	width: 520px;
	padding: 20px 30px 14px 30px;
}
.dialog-content-container:not(:has(.add-api-docs)) {
	height: 420px;
	width: 500px;
}

small {
	color: gray;
}
.primary-button {
	width: 80px !important;
	height: 35px !important;
	border-color: #5858ec !important;
	background-color: #5858ec !important;
	padding: 1rem;

	&,
	&.theme--dark.v-btn.v-btn--disabled.v-btn--has-bg {
		background-color: #5d5cfc !important;
		border-color: #5d5cfc !important;

		&:disabled {
			opacity: 0.5;
		}
	}

	&.no-padding {
		padding: 0px !important;
	}
}
::v-deep .app-combobox .v-select__slot .v-select__selections {
	padding-right: 5px !important;
}
.dialog-content {
	width: 100%;
	height: 100%;
}
</style>
